import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import { ESTIMATE_CHOICES, formatMinutes, bucketFor, EFFORT_META } from './choreEffort';
import RoutineSuggestions from './RoutineSuggestions';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function ChoreTemplateManager({ instance, categories, deadlinePresets, rooms = [], onChoresGenerated, showMessage }) {
  const { t, i18n } = useTranslation();
  const roomName = (room) => (i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn;
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetchJson(instance, '/api/chore-templates');
      setTemplates(unwrap(data));
    } catch (e) { console.error('Failed to load templates:', e); }
    finally { setLoading(false); }
  }, [instance]);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const handleSave = useCallback(async (data) => {
    try {
      const url = editingTemplate
        ? `/api/chore-templates/${editingTemplate.id}`
        : '/api/chore-templates';
      await apiFetchJson(instance, url, {
        method: editingTemplate ? 'PUT' : 'POST',
        body: JSON.stringify(data)
      });
      loadTemplates();
      setShowForm(false);
      setEditingTemplate(null);
      showMessage(editingTemplate ? t('chores.messages.templateUpdated') : t('chores.messages.templateCreated'));
    } catch (e) { console.error('Failed to save template:', e); }
  }, [instance, editingTemplate, loadTemplates, showMessage, t]);

  const handleDelete = useCallback(async (id) => {
    try {
      await apiFetchJson(instance, `/api/chore-templates/${id}`, { method: 'DELETE' });
      loadTemplates(); showMessage(t('chores.messages.templateDeactivated'));
    } catch (e) { console.error('Failed to delete template:', e); }
  }, [instance, loadTemplates, showMessage, t]);

  return (
    <div className="chore-templates">
      <div className="chore-templates-header">
        <h3>🔄 {t('chores.templates.title')}</h3>
        <button className="chore-btn chore-btn-primary" onClick={() => { setShowForm(true); setEditingTemplate(null); }}>
          {t('chores.templates.newTemplate')}
        </button>
      </div>

      {loading && <div className="chore-loading"><div className="chore-spinner"></div>{t('chores.templates.loadingTemplates')}</div>}

      <RoutineSuggestions
        instance={instance}
        rooms={rooms}
        showMessage={showMessage}
        onAdopted={loadTemplates}
      />

      {!loading && templates.length === 0 && (
        <div className="chore-empty">
          <p>{t('chores.templates.emptyMessage')}</p>
        </div>
      )}

      <div className="chore-template-list">
        {templates.map(template => {
          const category = categories.find(c => c.id === template.categoryId);
          const room = rooms.find(r => r.id === template.roomId);
          const effortBucket = bucketFor(template.estimatedMinutes);
          const checklistItems = template.checklistItems?.$values || template.checklistItems || [];
          return (
            <div key={template.id} className={`chore-template-card ${!template.isActive ? 'chore-template-inactive' : ''}`}>
              <div className="chore-template-info">
                <span className="chore-card-icon">{category?.icon || '📌'}</span>
                <div>
                  <strong>{template.title}</strong>
                  <span className="chore-template-frequency">{t('chores.templates.everyDays', { days: template.frequencyDays })} ({template.frequencyLabel})</span>
                  {room && (
                    <span className="chore-meta-item" title={t('chores.createModal.room')}>
                      {room.icon || '🏠'} {roomName(room)}
                    </span>
                  )}
                  {template.estimatedMinutes != null && (
                    <span
                      className={`chore-meta-item chore-meta-effort ${EFFORT_META[effortBucket].className}`}
                      title={t(`chores.effort.${effortBucket}`)}
                    >
                      {EFFORT_META[effortBucket].icon} {formatMinutes(template.estimatedMinutes, t)}
                    </span>
                  )}
                  {checklistItems.length > 0 && (
                    <span className="chore-template-checklist-count">☑ {t('chores.templates.checklistItemCount', { count: checklistItems.length })}</span>
                  )}
                  {!template.isActive && <span className="chore-badge chore-badge-inactive">{t('chores.templates.paused')}</span>}
                </div>
              </div>
              <div className="chore-template-actions">
                <button className="chore-btn chore-btn-secondary chore-btn-small" onClick={() => { setEditingTemplate(template); setShowForm(true); }}>✏️</button>
                <button className="chore-btn chore-btn-danger chore-btn-small" onClick={() => handleDelete(template.id)}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <TemplateForm
          template={editingTemplate}
          categories={categories}
          deadlinePresets={deadlinePresets}
          rooms={rooms}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingTemplate(null); }}
        />
      )}
    </div>
  );
}

function TemplateForm({ template, categories, deadlinePresets, rooms = [], onSave, onClose }) {
  const { t, i18n } = useTranslation();
  const roomName = (room) => (i18n.language === 'no' ? room.nameNo : room.nameEn) || room.nameEn;
  const frequencyPresets = [
    { days: 1, label: t('chores.templates.frequencyDaily') },
    { days: 7, label: t('chores.templates.frequencyWeekly') },
    { days: 14, label: t('chores.templates.frequencyBiweekly') },
    { days: 30, label: t('chores.templates.frequencyMonthly') }
  ];
  const [title, setTitle] = useState(template?.title || '');
  const [description, setDescription] = useState(template?.description || '');
  const [categoryId, setCategoryId] = useState(template?.categoryId || categories[0]?.id || '');
  const [frequencyDays, setFrequencyDays] = useState(template?.frequencyDays || 7);
  const [frequencyLabel, setFrequencyLabel] = useState(template?.frequencyLabel || t('chores.templates.frequencyWeekly'));
  const [priority, setPriority] = useState(template?.priority ?? 1);
  const [deadlinePresetId, setDeadlinePresetId] = useState(template?.deadlinePresetId || '');
  const [roomId, setRoomId] = useState(template?.roomId || '');
  const [estimatedMinutes, setEstimatedMinutes] = useState(template?.estimatedMinutes ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [checklistItems, setChecklistItems] = useState([]);
  const [newItem, setNewItem] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !categoryId) return;
    onSave({
      title, description: description || null, categoryId,
      frequencyDays, frequencyLabel, priority,
      deadlinePresetId: deadlinePresetId || null,
      roomId: roomId || null,
      estimatedMinutes: estimatedMinutes === '' ? null : parseInt(estimatedMinutes, 10),
      isActive,
      checklistItems: checklistItems.length > 0 ? checklistItems : null
    });
  };

  return (
    <div className="chore-modal-overlay" onClick={onClose}>
      <div className="chore-modal" onClick={(e) => e.stopPropagation()}>
        <div className="chore-modal-header">
          <h3>{template ? `✏️ ${t('chores.templates.editTemplate')}` : `➕ ${t('chores.templates.newRecurringTemplate')}`}</h3>
          <button className="chore-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} className="chore-modal-form">
          <div className="chore-form-group">
            <label>{t('chores.templates.titleLabel')}</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required autoFocus className="chore-input" placeholder={t('chores.templates.titlePlaceholder')} />
          </div>
          <div className="chore-form-group">
            <label>{t('chores.templates.description')}</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} className="chore-textarea" rows={2} />
          </div>
          <div className="chore-form-row">
            <div className="chore-form-group">
              <label>{t('chores.templates.category')}</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="chore-select" required>
                <option value="" disabled>{t('chores.templates.selectCategory')}</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.icon} {cat.nameEn}</option>)}
              </select>
            </div>
            <div className="chore-form-group">
              <label>{t('chores.templates.frequency')}</label>
              <select value={frequencyDays} onChange={(e) => {
                const days = parseInt(e.target.value);
                setFrequencyDays(days);
                setFrequencyLabel(frequencyPresets.find(f => f.days === days)?.label || t('chores.templates.everyDays', { days }));
              }} className="chore-select">
                {frequencyPresets.map(f => <option key={f.days} value={f.days}>{f.label}</option>)}
              </select>
            </div>
          </div>
          <div className="chore-form-row">
            <div className="chore-form-group">
              <label>{t('chores.templates.defaultDeadline')}</label>
              <select value={deadlinePresetId} onChange={(e) => setDeadlinePresetId(e.target.value)} className="chore-select">
                <option value="">{t('chores.templates.none')}</option>
                {deadlinePresets.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="chore-form-group">
              <label>{t('chores.templates.priority')}</label>
              <select value={priority} onChange={(e) => setPriority(parseInt(e.target.value))} className="chore-select">
                <option value={0}>{t('chores.priority.low')} ⚪</option>
                <option value={1}>{t('chores.priority.medium')} 🟡</option>
                <option value={2}>{t('chores.priority.high')} 🔴</option>
              </select>
            </div>
          </div>
          <div className="chore-form-row">
            <div className="chore-form-group">
              <label>{t('chores.createModal.room')}</label>
              <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className="chore-select">
                <option value="">{t('chores.createModal.noRoom')}</option>
                {rooms.map(room => (
                  <option key={room.id} value={room.id}>{room.icon} {roomName(room)}</option>
                ))}
              </select>
            </div>
            <div className="chore-form-group">
              <label>{t('chores.createModal.estimate')}</label>
              <select value={estimatedMinutes} onChange={(e) => setEstimatedMinutes(e.target.value)} className="chore-select">
                <option value="">{t('chores.effort.noEstimate')}</option>
                {ESTIMATE_CHOICES.map(minutes => (
                  <option key={minutes} value={minutes}>{formatMinutes(minutes, t)}</option>
                ))}
              </select>
            </div>
          </div>
          {template && (
            <div className="chore-form-group">
              <label>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> {t('chores.templates.activeLabel')}
              </label>
            </div>
          )}
          {!template && (
            <div className="chore-form-group">
              <label>{t('chores.templates.checklistLabel')}</label>
              <div className="chore-checklist-builder">
                {checklistItems.map((item, idx) => (
                  <div key={idx} className="chore-checklist-builder-item">
                    <span>☐ {item}</span>
                    <button type="button" className="chore-btn chore-btn-danger chore-btn-tiny" onClick={() => setChecklistItems(checklistItems.filter((_, i) => i !== idx))}>✕</button>
                  </div>
                ))}
                <div className="chore-add-item">
                  <input type="text" value={newItem} onChange={(e) => setNewItem(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newItem.trim()) { setChecklistItems([...checklistItems, newItem.trim()]); setNewItem(''); } } }} placeholder={t('chores.templates.addSubTask')} className="chore-add-item-input" />
                  <button type="button" className="chore-btn chore-btn-small" onClick={() => { if (newItem.trim()) { setChecklistItems([...checklistItems, newItem.trim()]); setNewItem(''); } }}>+</button>
                </div>
              </div>
            </div>
          )}
          <div className="chore-modal-actions">
            <button type="button" className="chore-btn chore-btn-secondary" onClick={onClose}>{t('chores.templates.cancel')}</button>
            <button type="submit" className="chore-btn chore-btn-primary" disabled={!title.trim() || !categoryId}>{template ? t('chores.templates.save') : t('chores.templates.createTemplate')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChoreTemplateManager;
